import { CircleNotchIcon } from "@phosphor-icons/react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import {
  Combobox,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
  ComboboxSeparator,
} from "@/components/ui/combobox";
import { FieldErrorText, Label } from "@/components/ui/field";
import { cn } from "@/lib/utils";
import {
  groupTimezoneOptions,
  resolveLocalTimezoneValue,
  type TimezoneComboboxGroup,
  type TimezoneComboboxOption,
} from "./timezoneComboboxHelpers";

export type { TimezoneComboboxOption } from "./timezoneComboboxHelpers";

const filterOptionsByQuery = (
  options: TimezoneComboboxOption[],
  query: string,
): TimezoneComboboxOption[] => {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return options;

  return options.filter((option) =>
    option.label.toLowerCase().includes(normalizedQuery),
  );
};

const labelForTimezoneValue = (
  options: TimezoneComboboxOption[],
  timezoneValue: string | null,
): string => {
  if (!timezoneValue) return "";
  return options.find((option) => option.value === timezoneValue)?.label ?? "";
};

const groupsWithItems = (
  groups: TimezoneComboboxGroup[],
): TimezoneComboboxGroup[] => groups.filter((group) => group.items.length > 0);

export interface TimezoneComboboxProps {
  options: TimezoneComboboxOption[];
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  label?: string;
  labelVariant?: "default" | "secondary";
  labelSize?: "sm" | "md" | "lg";
  placeholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  isPending?: boolean;
  /** Shows the clear control when a timezone is selected (replaces the chevron). */
  clearable?: boolean;
  errorMessage?: string;
  className?: string;
}

const TimezoneCombobox = ({
  options,
  value,
  defaultValue,
  onValueChange,
  label,
  labelVariant = "secondary",
  labelSize = "sm",
  placeholder = "Search or select timezone",
  emptyMessage = "No timezone found.",
  disabled = false,
  isPending = false,
  clearable = true,
  errorMessage,
  className,
}: TimezoneComboboxProps) => {
  const isControlled = value !== undefined;
  const comboboxInputId = useId();
  const hasAppliedLocalDefaultRef = useRef(false);

  const [uncontrolledValue, setUncontrolledValue] = useState<string | null>(
    () => {
      if (defaultValue !== undefined) return defaultValue || null;
      return resolveLocalTimezoneValue(options) ?? null;
    },
  );

  const selectedValue = isControlled ? value || null : uncontrolledValue;
  const selectedValueRef = useRef(selectedValue);

  useEffect(() => {
    selectedValueRef.current = selectedValue;
  }, [selectedValue]);

  const selectedOption = useMemo(
    () =>
      selectedValue
        ? (options.find((option) => option.value === selectedValue) ?? null)
        : null,
    [options, selectedValue],
  );

  const [inputValue, setInputValue] = useState(() =>
    labelForTimezoneValue(options, selectedValue),
  );
  const [showAllOptions, setShowAllOptions] = useState(false);

  useEffect(() => {
    if (!isControlled) return;
    setInputValue(labelForTimezoneValue(options, value || null));
  }, [isControlled, options, value]);

  useEffect(() => {
    if (
      hasAppliedLocalDefaultRef.current ||
      isControlled ||
      defaultValue !== undefined ||
      options.length === 0
    ) {
      return;
    }

    const localValue = resolveLocalTimezoneValue(options);
    if (!localValue) return;

    hasAppliedLocalDefaultRef.current = true;
    setUncontrolledValue(localValue);
    setInputValue(labelForTimezoneValue(options, localValue));
  }, [defaultValue, isControlled, options]);

  useEffect(() => {
    if (isControlled || !selectedValue || inputValue) return;
    const label = labelForTimezoneValue(options, selectedValue);
    if (label) setInputValue(label);
  }, [isControlled, inputValue, options, selectedValue]);

  const timezoneGroups = useMemo(
    () => groupTimezoneOptions(options),
    [options],
  );

  const filteredGroups = useMemo(() => {
    const groups = showAllOptions
      ? timezoneGroups
      : groupTimezoneOptions(filterOptionsByQuery(options, inputValue));
    return groupsWithItems(groups);
  }, [inputValue, options, showAllOptions, timezoneGroups]);

  const handleValueChange = (option: TimezoneComboboxOption | null) => {
    const nextValue = option?.value ?? "";
    selectedValueRef.current = option ? option.value : null;
    if (!isControlled) {
      setUncontrolledValue(option ? option.value : null);
    }
    setInputValue(option?.label ?? "");
    onValueChange?.(nextValue);
    setShowAllOptions(false);
  };

  const isInputDisabled = disabled || isPending;

  return (
    <div className={cn("flex flex-col gap-1", className)}>
      {label ? (
        <Label
          htmlFor={comboboxInputId}
          variant={labelVariant}
          size={labelSize}
        >
          {label}
        </Label>
      ) : null}

      <Combobox
        autoHighlight={true}
        items={timezoneGroups}
        filteredItems={filteredGroups}
        itemToStringValue={(option) => option.label}
        isItemEqualToValue={(item, selected) => item.value === selected.value}
        value={selectedOption}
        onValueChange={handleValueChange}
        inputValue={inputValue}
        onInputValueChange={(nextValue, eventDetails) => {
          setInputValue(nextValue);
          if (eventDetails.reason === "input-change") {
            setShowAllOptions(false);
          }
          if (nextValue === "" || eventDetails.reason === "clear-press") {
            handleValueChange(null);
          }
        }}
        onOpenChange={(open, eventDetails) => {
          if (open && eventDetails.reason === "trigger-press") {
            setShowAllOptions(true);
          }
          if (!open) {
            setShowAllOptions(false);
            setInputValue(
              labelForTimezoneValue(options, selectedValueRef.current),
            );
          }
        }}
        disabled={isInputDisabled}
      >
        <ComboboxInput
          id={comboboxInputId}
          className="w-full"
          placeholder={isPending ? "Loading..." : placeholder}
          showClear={clearable && !isPending}
          showTrigger={!isPending}
          disabled={isInputDisabled}
          aria-label={label ? undefined : "Timezone"}
          aria-invalid={errorMessage ? true : undefined}
          onFocus={() => {
            if (isInputDisabled) return;
            setShowAllOptions(true);
          }}
        >
          {isPending ? (
            <CircleNotchIcon
              className="mr-2 size-4 shrink-0 animate-spin text-slate-400"
              aria-hidden
            />
          ) : null}
        </ComboboxInput>
        <ComboboxContent>
          <ComboboxEmpty>{emptyMessage}</ComboboxEmpty>
          <ComboboxList>
            {(group, index) => (
              <ComboboxGroup key={group.value} items={group.items}>
                <ComboboxLabel>{group.value}</ComboboxLabel>
                <ComboboxCollection>
                  {(option) => (
                    <ComboboxItem key={option.value} value={option}>
                      {option.label}
                    </ComboboxItem>
                  )}
                </ComboboxCollection>
                {index < filteredGroups.length - 1 ? (
                  <ComboboxSeparator />
                ) : null}
              </ComboboxGroup>
            )}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
      {errorMessage ? <FieldErrorText>{errorMessage}</FieldErrorText> : null}
    </div>
  );
};

export default TimezoneCombobox;
